import React, { useMemo } from 'react';

// Conditional import for DOMPurify (only in browser environment)
let DOMPurify;
if (typeof window !== 'undefined') {
  DOMPurify = require('dompurify');
}

// مكون لتنظيف وعرض HTML بأمان
export const SafeHTML = ({ html, allowedTags = [], className = '', ...props }) => {
  const sanitizedHTML = useMemo(() => {
    if (!html) return '';

    // Fallback if DOMPurify is not available
    if (!DOMPurify) {
      // Basic HTML escaping as fallback
      return html.replace(/[<>]/g, '');
    }

    // إعداد DOMPurify مع العلامات المسموحة
    const config = {
      ALLOWED_TAGS: allowedTags.length > 0 ? allowedTags : ['p', 'br', 'strong', 'em', 'u', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
      FORBID_SCRIPT: true,
      FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
    };

    return DOMPurify.sanitize(html, config);
  }, [html, allowedTags]);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      {...props}
    />
  );
};

// دالة لتنظيف النصوص من المحتوى الضار
export const sanitizeText = (text, options = {}) => {
  if (!text || typeof text !== 'string') return '';

  const {
    maxLength = 1000,
    allowHTML = false,
    allowURLs = true,
    removeScripts = true
  } = options;

  let sanitized = text.trim();

  // تحديد الطول الأقصى
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // إزالة السكريبت والعلامات الخطيرة
  if (removeScripts) {
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }

  // تنظيف HTML إذا لم يكن مسموحاً
  if (!allowHTML) {
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  }

  // تنظيف URLs إذا لم تكن مسموحة
  if (!allowURLs) {
    sanitized = sanitized.replace(/https?:\/\/[^\s]+/gi, '[رابط محذوف]');
  }

  return sanitized;
};

// دالة للتحقق من صحة البريد الإلكتروني
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i,
    /on\w+=/i
  ];

  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'البريد الإلكتروني مطلوب' };
  }

  if (email.length > 254) {
    return { isValid: false, error: 'البريد الإلكتروني طويل جداً' };
  }

  if (dangerousPatterns.some(pattern => pattern.test(email))) {
    return { isValid: false, error: 'البريد الإلكتروني يحتوي على محتوى غير مسموح' };
  }

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'تنسيق البريد الإلكتروني غير صحيح' };
  }

  return { isValid: true };
};

// دالة للتحقق من صحة كلمة المرور
export const validatePassword = (password) => {
  const errors = [];

  if (!password || typeof password !== 'string') {
    return { isValid: false, errors: ['كلمة المرور مطلوبة'] };
  }

  if (password.length < 8) {
    errors.push('كلمة المرور يجب أن تكون 8 أحرف على الأقل');
  }

  if (password.length > 128) {
    errors.push('كلمة المرور طويلة جداً');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('كلمة المرور يجب أن تحتوي على حرف صغير واحد على الأقل');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('كلمة المرور يجب أن تحتوي على حرف كبير واحد على الأقل');
  }

  if (!/\d/.test(password)) {
    errors.push('كلمة المرور يجب أن تحتوي على رقم واحد على الأقل');
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('كلمة المرور يجب أن تحتوي على رمز خاص واحد على الأقل');
  }

  // التحقق من الأنماط الخطيرة
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i
  ];

  if (dangerousPatterns.some(pattern => pattern.test(password))) {
    errors.push('كلمة المرور تحتوي على محتوى غير مسموح');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// مكون للتحقق من الأمان في النماذج
export const SecureForm = ({ children, onSubmit, validateOnSubmit = true, ...props }) => {
  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateOnSubmit) {
      const formData = new FormData(event.target);
      const data = Object.fromEntries(formData.entries());

      // تنظيف جميع البيانات
      const sanitizedData = {};
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string') {
          sanitizedData[key] = sanitizeText(value, {
            maxLength: key === 'password' ? 128 : 1000,
            allowHTML: false,
            allowURLs: key === 'website' || key === 'url'
          });
        } else {
          sanitizedData[key] = value;
        }
      }

      if (onSubmit) {
        onSubmit(sanitizedData, event);
      }
    } else {
      if (onSubmit) {
        onSubmit(event);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} {...props}>
      {children}
    </form>
  );
};

// Hook للتحقق من الأمان في الوقت الفعلي
export const useSecurityValidation = () => {
  const validateInput = (value, type = 'text', options = {}) => {
    switch (type) {
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      case 'text':
      default:
        const sanitized = sanitizeText(value, options);
        return {
          isValid: sanitized === value,
          sanitized,
          error: sanitized !== value ? 'تم تنظيف المحتوى المدخل' : null
        };
    }
  };

  const sanitizeObject = (obj, rules = {}) => {
    const sanitized = {};

    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        const rule = rules[key] || {};
        sanitized[key] = sanitizeText(value, rule);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  };

  return {
    validateInput,
    sanitizeObject,
    sanitizeText,
    validateEmail,
    validatePassword
  };
};

// مكون لحماية المحتوى الحساس
export const ProtectedContent = ({
  children,
  requireAuth = true,
  requireVerification = false,
  fallback = null
}) => {
  // يمكن ربطه مع AuthContext للتحقق من الصلاحيات
  const isAuthenticated = true; // استبدال بالتحقق الفعلي
  const isVerified = true; // استبدال بالتحقق الفعلي

  if (requireAuth && !isAuthenticated) {
    return fallback || <div>يجب تسجيل الدخول لعرض هذا المحتوى</div>;
  }

  if (requireVerification && !isVerified) {
    return fallback || <div>يجب تفعيل الحساب لعرض هذا المحتوى</div>;
  }

  return children;
};

// دالة لتشفير البيانات الحساسة قبل التخزين المحلي
export const secureStorage = {
  setItem: (key, value) => {
    try {
      // تشفير بسيط (يفضل استخدام مكتبة تشفير أقوى في الإنتاج)
      const encrypted = btoa(JSON.stringify(value));
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Error storing secure data:', error);
    }
  },

  getItem: (key) => {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;

      const decrypted = atob(encrypted);
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('Error retrieving secure data:', error);
      return null;
    }
  },

  removeItem: (key) => {
    localStorage.removeItem(key);
  }
};

// مكون لمراقبة الأنشطة المشبوهة
export const SecurityMonitor = ({ children }) => {
  React.useEffect(() => {
    let suspiciousActivity = 0;

    const monitorActivity = (event) => {
      // مراقبة المحاولات المشبوهة
      if (event.type === 'paste') {
        const pastedData = event.clipboardData?.getData('text') || '';
        if (/<script|javascript:|vbscript:/i.test(pastedData)) {
          event.preventDefault();
          suspiciousActivity++;
          console.warn('Suspicious paste activity detected');
        }
      }

      // مراقبة محاولات فتح أدوات المطور
      if (event.key === 'F12' ||
          (event.ctrlKey && event.shiftKey && event.key === 'I') ||
          (event.ctrlKey && event.shiftKey && event.key === 'C')) {
        suspiciousActivity++;
        if (suspiciousActivity > 3) {
          console.warn('Multiple developer tools access attempts detected');
        }
      }
    };

    document.addEventListener('paste', monitorActivity);
    document.addEventListener('keydown', monitorActivity);

    return () => {
      document.removeEventListener('paste', monitorActivity);
      document.removeEventListener('keydown', monitorActivity);
    };
  }, []);

  return children;
};

// Default export - Security Monitor component
export default SecurityMonitor;
